import Dialog from "../../components/General/ConfirmableDialog";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import {
  ArrowBackIosNew,
  ArrowForwardIos,
  Delete,
  Edit,
  Groups,
} from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import {
  createExternalEvent,
  deleteExternalEvent,
  getExternalEventGoingEmployees,
  getExternalEventInterestedEmployees,
  getExternalEvents,
  markGoingToExternalEvent,
  markInterestedInExternalEvent,
  unmarkGoingToExternalEvent,
  unmarkInterestedInExternalEvent,
  updateExternalEvent,
} from "../../api/externalEventService";
import {
  ExternalEventCreateUpdateDto,
  ExternalEventDto,
} from "../../interfaces/HR/externalEvent";

const formatDate = (value: string) => dayjs(value).format("MMM D, YYYY");

const departments = [
  "Safety",
  "HR",
  "Brokerage",
  "Intermodal",
  "Accounting",
  "Sales",
  "Dispatch",
  "Data Analytics",
  "Fleet",
  "Permits",
  "IT",
  "Management",
];

const emptyForm: ExternalEventCreateUpdateDto = {
  eventName: "",
  location: "",
  website: "",
  eventDate: dayjs().format("YYYY-MM-DD"),
  eventStartDate: dayjs().format("YYYY-MM-DD"),
  eventEndDate: dayjs().format("YYYY-MM-DD"),
  details: "",
  suitableDepartment: "",
};

const getWebsiteUrl = (website: string) => {
  if (!website) return "";
  const trimmed = website.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const parseDepartments = (value: string) =>
  value
    .split(",")
    .map((department) => department.trim())
    .filter(Boolean);

const ExternalEventsPage = () => {
  const { token, role } = useAuth();
  const [searchParams] = useSearchParams();
  const initialSelectedDateParam = searchParams.get("date");
  const initialSelectedDate =
    initialSelectedDateParam && dayjs(initialSelectedDateParam).isValid()
      ? dayjs(initialSelectedDateParam).startOf("day")
      : dayjs().startOf("day");
  const canManageEvents = role?.includes("HR") ?? false;
  const queryClient = useQueryClient();

  const [currentMonth, setCurrentMonth] = useState<Dayjs>(
    initialSelectedDate.startOf("month"),
  );
  const [selectedDate, setSelectedDate] = useState<Dayjs>(initialSelectedDate);
  const [formValues, setFormValues] =
    useState<ExternalEventCreateUpdateDto>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [interestedEvent, setInterestedEvent] =
    useState<ExternalEventDto | null>(null);
  const [goingEvent, setGoingEvent] = useState<ExternalEventDto | null>(null);
  const [eventStatusError, setEventStatusError] = useState<string | null>(null);

  const {
    data: events = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["externalEvents"],
    queryFn: () => {
      if (!token) throw new Error("No token");
      return getExternalEvents(token);
    },
    enabled: !!token,
  });

  const getEventStartDate = (event: ExternalEventDto) =>
    event.eventStartDate ?? event.eventDate ?? dayjs().format("YYYY-MM-DD");

  const getEventEndDate = (event: ExternalEventDto) =>
    event.eventEndDate ??
    event.eventStartDate ??
    event.eventDate ??
    dayjs().format("YYYY-MM-DD");

  const getEventDepartments = (event: ExternalEventDto) =>
    parseDepartments(event.suitableDepartment);

  const sortedEvents = useMemo(
    () =>
      [...events].sort(
        (a, b) =>
          dayjs(getEventStartDate(a)).valueOf() -
          dayjs(getEventStartDate(b)).valueOf(),
      ),
    [events],
  );

  const upcomingEvents = useMemo(
    () =>
      sortedEvents
        .filter((event) =>
          dayjs(getEventEndDate(event)).isAfter(
            dayjs().subtract(1, "day"),
            "day",
          ),
        )
        .slice(0, 8),
    [sortedEvents],
  );

  const eventsByDate = useMemo(() => {
    const map = new Map<string, ExternalEventDto[]>();
    sortedEvents.forEach((event) => {
      const startDate = dayjs(getEventStartDate(event)).startOf("day");
      const endDate = dayjs(getEventEndDate(event)).startOf("day");

      if (endDate.isBefore(startDate, "day")) {
        const key = startDate.format("YYYY-MM-DD");
        const current = map.get(key) ?? [];
        map.set(key, [...current, event]);
        return;
      }

      let currentDay = startDate;
      while (
        currentDay.isSame(endDate, "day") ||
        currentDay.isBefore(endDate, "day")
      ) {
        const key = currentDay.format("YYYY-MM-DD");
        const current = map.get(key) ?? [];
        map.set(key, [...current, event]);
        currentDay = currentDay.add(1, "day");
      }
    });
    return map;
  }, [sortedEvents]);

  const selectedEvents =
    eventsByDate.get(selectedDate.format("YYYY-MM-DD")) ?? [];

  const calendarDays = useMemo(() => {
    const start = currentMonth.startOf("month").startOf("week");
    return Array.from({ length: 42 }, (_, index) => start.add(index, "day"));
  }, [currentMonth]);

  const refetchEvents = () =>
    queryClient.invalidateQueries({ queryKey: ["externalEvents"] });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error("No token");
      const payload: ExternalEventCreateUpdateDto = {
        ...formValues,
        eventDate: formValues.eventStartDate,
      };
      if (editingId) {
        return updateExternalEvent(token, { ...payload, id: editingId });
      }
      return createExternalEvent(token, payload);
    },
    onSuccess: () => {
      setFormValues(emptyForm);
      setEditingId(null);
      refetchEvents();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("No token");
      await deleteExternalEvent(token, id);
    },
    onSuccess: refetchEvents,
  });

  const { data: eventParticipants = {} } = useQuery({
    queryKey: [
      "externalEventParticipants",
      events.map((event) => event.id).join(","),
    ],
    queryFn: async () => {
      if (!token) throw new Error("No token");
      const entries = await Promise.all(
        events.map(async (event) => {
          const [interested, going] = await Promise.all([
            getExternalEventInterestedEmployees(token, event.id),
            getExternalEventGoingEmployees(token, event.id),
          ]);
          return [event.id, { interested, going }] as const;
        }),
      );
      return Object.fromEntries(entries);
    },
    enabled: !!token && events.length > 0,
  });

  const getEmployeeId = () => {
    const employeeId = localStorage.getItem("employeeId");
    if (!employeeId) {
      throw new Error(
        "Employee id was not found. Please visit your HR profile page or re-login.",
      );
    }
    return employeeId;
  };

  const refreshEventStatus = () => {
    refetchEvents();
    queryClient.invalidateQueries({ queryKey: ["externalEventParticipants"] });
    queryClient.invalidateQueries({ queryKey: ["eventInterested"] });
    queryClient.invalidateQueries({ queryKey: ["eventGoing"] });
  };

  const interestedMutation = useMutation({
    mutationFn: async ({
      eventId,
      active,
    }: {
      eventId: string;
      active: boolean;
    }) => {
      if (!token) throw new Error("No token");
      const employeeId = getEmployeeId();
      if (active) {
        await unmarkInterestedInExternalEvent(token, eventId, employeeId);
        return;
      }
      await unmarkGoingToExternalEvent(token, eventId, employeeId);
      await markInterestedInExternalEvent(token, eventId, employeeId);
    },
    onSuccess: () => {
      setEventStatusError(null);
      refreshEventStatus();
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update event status. Please try again.";
      setEventStatusError(message);
    },
  });

  const goingMutation = useMutation({
    mutationFn: async ({
      eventId,
      active,
    }: {
      eventId: string;
      active: boolean;
    }) => {
      if (!token) throw new Error("No token");
      const employeeId = getEmployeeId();
      if (active) {
        await unmarkGoingToExternalEvent(token, eventId, employeeId);
        return;
      }
      await unmarkInterestedInExternalEvent(token, eventId, employeeId);
      await markGoingToExternalEvent(token, eventId, employeeId);
    },
    onSuccess: () => {
      setEventStatusError(null);
      refreshEventStatus();
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update event status. Please try again.";
      setEventStatusError(message);
    },
  });

  const { data: interestedEmployees = [], isLoading: interestedLoading } =
    useQuery({
      queryKey: ["eventInterested", interestedEvent?.id],
      queryFn: () => {
        if (!token || !interestedEvent?.id) throw new Error("Missing data");
        return getExternalEventInterestedEmployees(token, interestedEvent.id);
      },
      enabled: !!token && !!interestedEvent?.id,
    });

  const { data: goingEmployees = [], isLoading: goingLoading } = useQuery({
    queryKey: ["eventGoing", goingEvent?.id],
    queryFn: () => {
      if (!token || !goingEvent?.id) throw new Error("Missing data");
      return getExternalEventGoingEmployees(token, goingEvent.id);
    },
    enabled: !!token && !!goingEvent?.id,
  });

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Typography variant="h4" fontWeight={700} mb={1}>
        External Events
      </Typography>
      <Typography color="text.secondary" mb={3}>
        Track outside events to attend.
      </Typography>

      {error && <Alert severity="error">{(error as Error).message}</Alert>}

      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", lg: "2fr 1fr" }}
        gap={3}
      >
        <Box>
          <Paper sx={{ p: 2.5, borderRadius: 3 }} elevation={3}>
            <Stack
              flexWrap={{ xs: "wrap", lg: "nowrap" }}
              useFlexGap
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <IconButton
                onClick={() =>
                  setCurrentMonth((previous) => previous.subtract(1, "month"))
                }
              >
                <ArrowBackIosNew fontSize="small" />
              </IconButton>
              <Typography variant="h6" fontWeight={700}>
                {currentMonth.format("MMMM YYYY")}
              </Typography>
              <IconButton
                onClick={() =>
                  setCurrentMonth((previous) => previous.add(1, "month"))
                }
              >
                <ArrowForwardIos fontSize="small" />
              </IconButton>
            </Stack>

            <Box
              display="grid"
              gridTemplateColumns="repeat(7, minmax(0, 1fr))"
              gap={1}
              mb={1}
            >
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <Box key={day}>
                  <Typography
                    align="center"
                    fontWeight={700}
                    color="text.secondary"
                  >
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              display="grid"
              gridTemplateColumns="repeat(7, minmax(0, 1fr))"
              gap={1}
            >
              {calendarDays.map((day) => {
                const key = day.format("YYYY-MM-DD");
                const dayEvents = eventsByDate.get(key) ?? [];
                const isCurrentMonth = day.month() === currentMonth.month();
                const isSelected = selectedDate.isSame(day, "day");

                return (
                  <Box key={key}>
                    <Card
                      onClick={() => setSelectedDate(day)}
                      sx={{
                        cursor: "pointer",
                        minHeight: 90,
                        border: isSelected ? "2px solid" : "1px solid",
                        borderColor: isSelected ? "primary.main" : "divider",
                        opacity: isCurrentMonth ? 1 : 0.4,
                      }}
                    >
                      <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
                        <Typography variant="body2" fontWeight={700}>
                          {day.date()}
                        </Typography>
                        {dayEvents.slice(0, 2).map((event) => (
                          <Chip
                            key={event.id}
                            label={event.eventName}
                            size="small"
                            color="primary"
                            sx={{ mt: 0.5, maxWidth: "100%" }}
                          />
                        ))}
                        {dayEvents.length > 2 && (
                          <Typography variant="caption" color="text.secondary">
                            +{dayEvents.length - 2} more
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>

        <Box>
          <Paper sx={{ p: 2.5, borderRadius: 3 }} elevation={3}>
            <Typography variant="h6" fontWeight={700}>
              {selectedDate.format("MMMM D, YYYY")}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {eventStatusError && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {eventStatusError}
              </Alert>
            )}
            {selectedEvents.length === 0 ? (
              <Typography color="text.secondary">
                No events on this date.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {selectedEvents.map((event) => {
                  const employeeId = localStorage.getItem("employeeId");
                  const participants = eventParticipants[event.id];
                  const isInterested =
                    !!employeeId &&
                    participants?.interested.some(
                      (employee) => employee.employeeId === employeeId,
                    );
                  const isGoing =
                    !!employeeId &&
                    participants?.going.some(
                      (employee) => employee.employeeId === employeeId,
                    );
                  const interestedCount =
                    participants?.interested.length ??
                    event.interestedCount ??
                    0;
                  const goingCount =
                    participants?.going.length ?? event.goingCount ?? 0;

                  return (
                    <Card key={event.id} variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {event.eventName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(getEventStartDate(event))} -{" "}
                          {formatDate(getEventEndDate(event))}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mb={0.7}
                        >
                          {event.location}
                        </Typography>
                        <Typography variant="body2" mb={0.7}>
                          Departments:{" "}
                          {getEventDepartments(event).join(", ") || "N/A"}
                        </Typography>
                        <Typography variant="body2" mb={0.7}>
                          {event.details}
                        </Typography>
                        {event.website && (
                          <Link
                            href={getWebsiteUrl(event.website)}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            display="block"
                            sx={{ mb: 0.8, wordBreak: "break-all" }}
                          >
                            {event.website}
                          </Link>
                        )}
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            size="small"
                            variant={isInterested ? "contained" : "outlined"}
                            color={isInterested ? "success" : "primary"}
                            disabled={
                              interestedMutation.isPending ||
                              goingMutation.isPending
                            }
                            onClick={() =>
                              interestedMutation.mutate({
                                eventId: event.id,
                                active: isInterested,
                              })
                            }
                          >
                            {interestedMutation.isPending
                              ? "Saving..."
                              : isInterested
                                ? "Interested"
                                : "Interested"}
                          </Button>
                          <Button
                            size="small"
                            variant={isGoing ? "contained" : "outlined"}
                            color={isGoing ? "success" : "primary"}
                            disabled={
                              interestedMutation.isPending ||
                              goingMutation.isPending
                            }
                            onClick={() =>
                              goingMutation.mutate({
                                eventId: event.id,
                                active: isGoing,
                              })
                            }
                          >
                            {goingMutation.isPending
                              ? "Saving..."
                              : isGoing
                                ? "Going"
                                : "Going"}
                          </Button>
                          <Button
                            size="small"
                            startIcon={<Groups />}
                            onClick={() => setInterestedEvent(event)}
                          >
                            {interestedCount} interested
                          </Button>
                          <Button
                            size="small"
                            startIcon={<Groups />}
                            onClick={() => setGoingEvent(event)}
                          >
                            {goingCount} going
                          </Button>
                          {canManageEvents && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingId(event.id);
                                  setFormValues({
                                    id: event.id,
                                    website: event.website,
                                    eventName: event.eventName,
                                    location: event.location,
                                    details: event.details,
                                    suitableDepartment:
                                      event.suitableDepartment,
                                    eventDate: dayjs(event.eventDate).format(
                                      "YYYY-MM-DD",
                                    ),
                                    eventStartDate: dayjs(
                                      getEventStartDate(event),
                                    ).format("YYYY-MM-DD"),
                                    eventEndDate: dayjs(
                                      getEventEndDate(event),
                                    ).format("YYYY-MM-DD"),
                                  });
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => deleteMutation.mutate(event.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Paper>

          {canManageEvents && (
            <Paper sx={{ p: 2.5, borderRadius: 3, mt: 2 }} elevation={3}>
              <Typography variant="h6" fontWeight={700} mb={1.5}>
                {editingId ? "Edit event" : "Add event"}
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Website"
                  value={formValues.website}
                  onChange={(event) =>
                    setFormValues((previous) => ({
                      ...previous,
                      website: event.target.value,
                    }))
                  }
                  fullWidth
                />
                <TextField
                  label="Event name"
                  value={formValues.eventName}
                  onChange={(event) =>
                    setFormValues((previous) => ({
                      ...previous,
                      eventName: event.target.value,
                    }))
                  }
                  fullWidth
                />
                <TextField
                  label="Start date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formValues.eventStartDate}
                  onChange={(event) =>
                    setFormValues((previous) => ({
                      ...previous,
                      eventStartDate: event.target.value,
                      eventDate: event.target.value,
                    }))
                  }
                  fullWidth
                />
                <TextField
                  label="End date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formValues.eventEndDate}
                  onChange={(event) =>
                    setFormValues((previous) => ({
                      ...previous,
                      eventEndDate: event.target.value,
                    }))
                  }
                  fullWidth
                />
                <TextField
                  label="Location"
                  value={formValues.location}
                  onChange={(event) =>
                    setFormValues((previous) => ({
                      ...previous,
                      location: event.target.value,
                    }))
                  }
                  fullWidth
                />
                <TextField
                  label="Details"
                  value={formValues.details}
                  onChange={(event) =>
                    setFormValues((previous) => ({
                      ...previous,
                      details: event.target.value,
                    }))
                  }
                  multiline
                  minRows={2}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel id="departments-label">
                    Suitable departments
                  </InputLabel>
                  <Select
                    labelId="departments-label"
                    multiple
                    value={parseDepartments(formValues.suitableDepartment)}
                    onChange={(event) =>
                      setFormValues((previous) => ({
                        ...previous,
                        suitableDepartment:
                          typeof event.target.value === "string"
                            ? event.target.value
                            : event.target.value.join(", "),
                      }))
                    }
                    input={<OutlinedInput label="Suitable departments" />}
                    renderValue={(selected) => selected.join(", ")}
                  >
                    {departments.map((department) => (
                      <MenuItem key={department} value={department}>
                        {department}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack
                  flexWrap={{ xs: "wrap", lg: "nowrap" }}
                  useFlexGap
                  direction="row"
                  spacing={1}
                >
                  <Button
                    variant="contained"
                    onClick={() => saveMutation.mutate()}
                    disabled={
                      !formValues.eventName ||
                      !formValues.eventStartDate ||
                      !formValues.eventEndDate ||
                      !formValues.location ||
                      !formValues.website ||
                      !formValues.details ||
                      parseDepartments(formValues.suitableDepartment).length ===
                        0
                    }
                  >
                    {editingId ? "Save" : "Create"}
                  </Button>
                  {editingId && (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditingId(null);
                        setFormValues(emptyForm);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 2.5, borderRadius: 3, mt: 3 }} elevation={3}>
        <Typography variant="h6" fontWeight={700} mb={1}>
          Upcoming external events
        </Typography>
        <List>
          {isLoading ? (
            <CircularProgress size={22} />
          ) : upcomingEvents.length === 0 ? (
            <Typography color="text.secondary">No upcoming events.</Typography>
          ) : (
            upcomingEvents.map((event) => {
              const participants = eventParticipants[event.id];
              const interestedCount =
                participants?.interested.length ?? event.interestedCount ?? 0;
              const goingCount =
                participants?.going.length ?? event.goingCount ?? 0;

              return (
                <ListItem key={event.id} divider>
                  <ListItemText
                    primary={`${event.eventName} • ${formatDate(getEventStartDate(event))} - ${formatDate(getEventEndDate(event))}`}
                    secondary={`${event.location} • ${getEventDepartments(event).join(", ")}`}
                  />
                  <Stack
                    flexWrap={{ xs: "wrap", lg: "nowrap" }}
                    useFlexGap
                    direction="row"
                    spacing={1}
                  >
                    <Button
                      size="small"
                      onClick={() => setInterestedEvent(event)}
                    >
                      {interestedCount} interested
                    </Button>
                    <Button size="small" onClick={() => setGoingEvent(event)}>
                      {goingCount} going
                    </Button>
                  </Stack>
                </ListItem>
              );
            })
          )}
        </List>
      </Paper>

      <Dialog
        open={!!interestedEvent}
        onClose={() => setInterestedEvent(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Interested employees{" "}
          {interestedEvent ? `• ${interestedEvent.eventName}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {interestedLoading ? (
            <CircularProgress size={20} />
          ) : interestedEmployees.length === 0 ? (
            <Typography color="text.secondary">
              No employees have marked interest yet.
            </Typography>
          ) : (
            <List dense>
              {interestedEmployees.map((employee) => (
                <ListItem key={employee.employeeId} divider>
                  <ListItemText
                    primary={`${employee.employeeFirstName} ${employee.employeeLastName}`}
                    secondary={`${employee.department} • ${employee.position}`}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(employee.interestedAt)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterestedEvent(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!goingEvent}
        onClose={() => setGoingEvent(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Going employees {goingEvent ? `• ${goingEvent.eventName}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {goingLoading ? (
            <CircularProgress size={20} />
          ) : goingEmployees.length === 0 ? (
            <Typography color="text.secondary">
              No employees have marked going yet.
            </Typography>
          ) : (
            <List dense>
              {goingEmployees.map((employee) => (
                <ListItem key={employee.employeeId} divider>
                  <ListItemText
                    primary={`${employee.employeeFirstName} ${employee.employeeLastName}`}
                    secondary={`${employee.department} • ${employee.position}`}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(employee.goingAt)}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoingEvent(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExternalEventsPage;
